import { useState, useEffect, useCallback } from 'react';
import { apiRawFetch } from '../../main/client';

export interface TaxPeriod {
    id: number;
    propertyId: number;
    componentId: string;
    periodType: 'monthly' | 'quarterly' | 'yearly';
    periodStart: string;
    periodEnd: string;
    dueDate: string;
    estimatedAmount: number | null;
    targetAmount: number | null;
    currency: string;
    status: 'pending' | 'paid' | 'partial' | 'overdue';
    totalPaid: number;
    paymentsCount: number;
    created: string;
    updated: string;
}

export interface TaxPeriodFilters {
    year?: number;
    status?: 'pending' | 'paid' | 'partial' | 'overdue';
    componentId?: string;
}

interface GenerateResult {
    created: number;
    skipped: number;
    message: string;
}

export default function useTaxPeriods(propertyId: number | undefined, filters?: TaxPeriodFilters) {
    const [periods, setPeriods] = useState<TaxPeriod[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchPeriods = useCallback(async () => {
        if (!propertyId) {
            setPeriods([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams();
            if (filters?.year) params.append('year', filters.year.toString());
            if (filters?.status) params.append('status', filters.status);
            if (filters?.componentId) params.append('componentId', filters.componentId);

            const queryString = params.toString();
            const url = `api/properties/${propertyId}/tax-periods${queryString ? `?${queryString}` : ''}`;

            const response = await apiRawFetch(url);

            if (!response.ok) {
                throw new Error('Failed to fetch tax periods');
            }

            const { data } = await response.json();
            setPeriods(data);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to fetch tax periods';
            setError(message);
            setPeriods([]);
        } finally {
            setLoading(false);
        }
    }, [propertyId, filters?.year, filters?.status, filters?.componentId]);

    useEffect(() => {
        fetchPeriods();
    }, [fetchPeriods]);

    const generatePeriods = useCallback(async (year: number): Promise<GenerateResult> => {
        if (!propertyId) {
            throw new Error('Property ID is required');
        }

        const response = await apiRawFetch(
            `api/properties/${propertyId}/tax-periods/generate`,
            'POST',
            { year }
        );

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to generate periods');
        }

        const { data } = await response.json();

        // Refresh the list after generating
        await fetchPeriods();

        return data;
    }, [propertyId, fetchPeriods]);

    const regeneratePeriods = useCallback(async (year: number): Promise<GenerateResult & { deleted?: number }> => {
        if (!propertyId) {
            throw new Error('Property ID is required');
        }

        const response = await apiRawFetch(
            `api/properties/${propertyId}/tax-periods/generate`,
            'POST',
            { year, force: true }
        );

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to regenerate periods');
        }

        const { data } = await response.json();

        await fetchPeriods();

        return data;
    }, [propertyId, fetchPeriods]);

    const updateTargetAmount = useCallback(async (periodId: number, targetAmount: number | null): Promise<void> => {
        if (!propertyId) {
            throw new Error('Property ID is required');
        }

        const response = await apiRawFetch(
            `api/properties/${propertyId}/tax-periods/${periodId}`,
            'PATCH',
            { targetAmount }
        );

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to update target amount');
        }

        await fetchPeriods();
    }, [propertyId, fetchPeriods]);

    return {
        periods,
        loading,
        error,
        refetch: fetchPeriods,
        generatePeriods,
        regeneratePeriods,
        updateTargetAmount,
    };
}
